import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Box,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Card,
  CardContent,
  Grid,
  LinearProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search as SearchIcon,
  GetApp as ExportIcon
} from '@material-ui/icons';
import * as XLSX from 'xlsx';
import { trackPromise } from 'react-promise-tracker';
import Alert from '../../../components/Alert';
import ConsultaPlazasService from '../../../services/ConsultaPlazasService';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  searchBox: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  searchField: {
    flex: 1,
  },
  exportButton: {
    backgroundColor: '#4caf50',
    color: 'white',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },

  metricValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  metricLabel: {
    fontSize: '0.9rem',
    opacity: 0.9,
  },
  // Tabla
  headerCell: {
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
    borderBottom: '2px solid #ddd',
  },
  // Indicadores de ocupación
  ocupacionBaja: {
    backgroundColor: '#e8f5e8',
    color: '#2e7d32',
  },
  ocupacionMedia: {
    backgroundColor: '#fff8e1',
    color: '#f57c00',
  },
  ocupacionAlta: {
    backgroundColor: '#ffebee',
    color: '#d32f2f',
  },
  // Estilos para expansión de universidades
  expandButton: {
    minWidth: 'auto',
    padding: '4px 10px',
    fontSize: '0.7rem',
    textTransform: 'none',
    borderRadius: '16px',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    border: '1px solid #bbdefb',
    marginTop: 6,
    alignSelf: 'flex-start',
    '&:hover': {
      backgroundColor: '#bbdefb',
      transform: 'scale(1.05)',
      boxShadow: '0 2px 4px rgba(25,118,210,0.2)',
    },
    transition: 'all 0.2s ease',
  },
  universidadesList: {
    maxWidth: '250px',
    minWidth: '200px',
    wordBreak: 'break-word',
    lineHeight: 1.4,
    padding: '8px 12px',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginTop: 4,
    textAlign: 'left',
  },
  universidadItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 4,
    padding: '2px 0',
    textAlign: 'left',
    '&:last-child': {
      marginBottom: 0,
    },
  },
}));

const Vista1ResumenEstrategico = ({ filtrosActivos = {} }) => {
  const classes = useStyles();
  
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());



  useEffect(() => {
    let isMounted = true;

    const loadDataSafe = async () => {
      try {
        setLoading(true);
        // Usar el endpoint correcto para resumen estratégico
        const result = await trackPromise(
          ConsultaPlazasService.getResumenPorServicioCarrera()
        );

        if (isMounted) {
          setData(result || []);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error al cargar resumen:', error);
          Alert.show({
            message: "Error al cargar el resumen por servicio-carrera",
            type: "error",
          });
          setData([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDataSafe();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    filterData();
  }, [data, searchTerm, filtrosActivos]);







  const filterData = () => {
    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        (item.servicio && item.servicio.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.carrera && item.carrera.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.lista_universidades && item.lista_universidades.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredData(filtered);
  };



  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Función para expandir/contraer universidades
  const toggleExpandRow = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  // Función helper para contar universidades correctamente
  const getUniversidadesCount = (listaUniversidades) => {
    if (!listaUniversidades || listaUniversidades === 'Sin solicitudes') {
      return 0;
    }
    return listaUniversidades
      .split(',')
      .map(u => u.trim())
      .filter(u => u && u.length > 0)
      .length;
  };

  // Función SIMPLIFICADA para renderizar lista de universidades
  const renderUniversidadesList = (item, index) => {
    const universidades = item.lista_universidades || 'Sin solicitudes';
    const isExpanded = expandedRows.has(index);

    // Sin solicitudes
    if (universidades === 'Sin solicitudes' || !universidades || universidades.trim() === '') {
      return (
        <Box className={classes.universidadesList} style={{ backgroundColor: '#f9f9f9', borderColor: '#ddd' }}>
          <Typography variant="caption" color="textSecondary" style={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
            📝 Sin solicitudes
          </Typography>
        </Box>
      );
    }

    // Procesar universidades
    const universidadesArray = universidades
      .split(',')
      .map(u => u.trim())
      .filter(u => u.length > 0);

    const total = universidadesArray.length;

    // Si hay 2 o menos, mostrar todas sin botón
    if (total <= 2) {
      return (
        <Box className={classes.universidadesList}>
          {universidadesArray.map((universidad, idx) => (
            <Box key={idx} className={classes.universidadItem}>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textAlign: 'left',
                  width: '100%'
                }}
              >
                🏛️ {universidad}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }

    // Si hay más de 2, mostrar con expansión
    const mostrar = isExpanded ? universidadesArray : universidadesArray.slice(0, 2);
    const ocultas = total - 2;

    return (
      <Box className={classes.universidadesList}>
        {/* Universidades visibles */}
        {mostrar.map((universidad, idx) => (
          <Box key={idx} className={classes.universidadItem}>
            <Typography
              variant="caption"
              color="textSecondary"
              style={{
                fontSize: '0.75rem',
                fontWeight: 500,
                textAlign: 'left',
                width: '100%'
              }}
            >
              🏛️ {universidad}
            </Typography>
          </Box>
        ))}

        {/* Indicador "y X más" solo si NO está expandido */}
        {!isExpanded && (
          <Typography
            variant="caption"
            color="textSecondary"
            style={{
              fontStyle: 'italic',
              fontSize: '0.7rem',
              marginBottom: 6,
              marginTop: 2,
              textAlign: 'left',
              width: '100%',
              paddingLeft: '4px'
            }}
          >
            ➕ y {ocultas} más...
          </Typography>
        )}

        {/* Botón simple */}
        <Button
          size="small"
          onClick={() => toggleExpandRow(index)}
          className={classes.expandButton}
        >
          {isExpanded ? '▲ Ver menos' : '▼ Ver más'}
        </Button>
      </Box>
    );
  };

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      Alert.show({
        message: "No hay datos para exportar",
        type: "warning",
      });
      return;
    }

    const exportData = filteredData.map(item => ({
      "Servicio": item.servicio || 'N/A',
      "Carrera": item.carrera || 'N/A',
      "Total Configurado": item.total_configurado || 0,
      "Disponibles": item.disponibles || 0,
      "En Gestión": item.en_gestion || 0,
      "Ocupadas": item.ocupadas || 0,
      "% Ocupación": item.porcentaje_ocupacion || 0,
      "Universidades Compitiendo": item.universidades_compitiendo || 0,
      "Lista Universidades": item.lista_universidades || 'N/A'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ResumenEstrategico");

    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `ResumenEstrategico_${fecha}.xlsx`);

    Alert.show({
      message: "Archivo Excel exportado exitosamente",
      type: "success",
    });
  };

  const getOcupacionStyle = (porcentaje) => {
    if (porcentaje < 30) return classes.ocupacionBaja;
    if (porcentaje < 70) return classes.ocupacionMedia;
    return classes.ocupacionAlta;
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>


      {/* Indicador de datos */}
      <Box mb={2} p={2} style={{
        backgroundColor: '#e3f2fd',
        borderRadius: 4,
        border: '2px solid #1976d2',
        textAlign: 'center'
      }}>
        <Typography variant="h6" style={{ color: '#1976d2', fontWeight: 'bold' }}>
          📊 VISTA ESTRATÉGICA: {filteredData.length} configuraciones servicio-carrera
        </Typography>
      </Box>

      <Paper>
        {/* Búsqueda y exportación */}
        <Box className={classes.searchBox}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            placeholder="Buscar por servicio, carrera o universidad..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <Tooltip title="Exportar a Excel">
            <Button
              variant="contained"
              className={classes.exportButton}
              startIcon={<ExportIcon />}
              onClick={exportToExcel}
              disabled={filteredData.length === 0}
            >
              Exportar
            </Button>
          </Tooltip>
        </Box>
        
        {/* Tabla de resumen */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell} align="left"><strong>Universidades</strong></TableCell>
                <TableCell className={classes.headerCell}><strong>Servicio</strong></TableCell>
                <TableCell className={classes.headerCell}><strong>Carrera</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>Configuradas</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>Disponibles</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>Aprobadas</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>Ocupadas</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>En Gestión</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>Rechazadas</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>% Ocupación</strong></TableCell>
                <TableCell className={classes.headerCell} align="center"><strong>Solicitudes</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={index} hover>
                    {/* Universidades Compitiendo - PRIMERA COLUMNA */}
                    <TableCell align="left">
                      <Box display="flex" flexDirection="column" alignItems="flex-start">
                        {(() => {
                          // Usar función helper para conteo correcto
                          const realCount = getUniversidadesCount(item.lista_universidades);

                          return (
                            <Tooltip
                              title={`${realCount} universidades han solicitado plazas para ${item.carrera} en ${item.servicio}`}
                              arrow
                            >
                              <Chip
                                label={`${realCount} universidades`}
                                style={{
                                  backgroundColor: realCount > 3 ? '#e8f5e8' : '#f5f5f5',
                                  color: realCount > 3 ? '#2e7d32' : '#666',
                                  fontWeight: 'bold',
                                  fontSize: '0.875rem',
                                  marginBottom: 8,
                                  cursor: 'help',
                                  alignSelf: 'flex-start'
                                }}
                              />
                            </Tooltip>
                          );
                        })()}
                        {renderUniversidadesList(item, index)}
                      </Box>
                    </TableCell>

                    {/* Servicio */}
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                        {item.servicio || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Código: {item.servicio_codigo || item.servicio_id || 'N/A'}
                      </Typography>
                    </TableCell>

                    {/* Carrera */}
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {item.carrera || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Código: {item.carrera_codigo || item.carrera_id || 'N/A'}
                      </Typography>
                    </TableCell>

                    {/* Total Configuradas */}
                    <TableCell align="center">
                      <Chip
                        label={item.total_configurado || 0}
                        style={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold', fontSize: '0.875rem' }}
                      />
                    </TableCell>

                    {/* Disponibles */}
                    <TableCell align="center">
                      <Chip
                        label={item.disponibles || 0}
                        style={{ backgroundColor: '#e8f5e8', color: '#2e7d32', fontWeight: 'bold', fontSize: '0.875rem' }}
                      />
                    </TableCell>

                    {/* Aprobadas */}
                    <TableCell align="center">
                      <Chip
                        label={item.aprobadas || 0}
                        style={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold', fontSize: '0.875rem' }}
                      />
                    </TableCell>

                    {/* Ocupadas */}
                    <TableCell align="center">
                      <Chip
                        label={item.ocupadas || 0}
                        style={{ backgroundColor: '#ffebee', color: '#d32f2f', fontWeight: 'bold', fontSize: '0.875rem' }}
                      />
                    </TableCell>

                    {/* En Gestión */}
                    <TableCell align="center">
                      <Chip
                        label={item.en_gestion || 0}
                        style={{ backgroundColor: '#fff8e1', color: '#f57c00', fontWeight: 'bold', fontSize: '0.875rem' }}
                      />
                    </TableCell>

                    {/* Rechazadas */}
                    <TableCell align="center">
                      <Chip
                        label={item.rechazadas || 0}
                        style={{ backgroundColor: '#fce4ec', color: '#c2185b', fontWeight: 'bold', fontSize: '0.875rem' }}
                      />
                    </TableCell>

                    {/* Porcentaje de Ocupación */}
                    <TableCell align="center">
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <Chip
                          label={`${item.porcentaje_ocupacion || 0}%`}
                          className={getOcupacionStyle(item.porcentaje_ocupacion || 0)}
                          style={{ fontWeight: 'bold', marginBottom: 4, fontSize: '0.875rem' }}
                        />
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(item.porcentaje_ocupacion || 0, 100)}
                          style={{ width: '60px', height: 4 }}
                        />
                      </Box>
                    </TableCell>

                    {/* Solicitudes - RESUMEN */}
                    <TableCell align="center">
                      <Box display="flex" flexDirection="column" alignItems="center">
                        <Typography variant="body2" style={{ fontWeight: 'bold', color: '#1976d2' }}>
                          {item.total_solicitudes || 0}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          solicitudes
                        </Typography>
                        <Typography variant="caption" color="textSecondary" style={{ marginTop: 2 }}>
                          {item.lugares_solicitados || 0} lugares
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
    </div>
  );
};

export default Vista1ResumenEstrategico;
